// A PESSOA DA EQUIPE numa despesa — a régua da TELA (05/10/2026).
//
// Por que existe: quem ligava a despesa de remuneração à ficha da pessoa era o
// sync do Conta Azul, morto desde 15/07/2026. Nenhuma tela gravava a pessoa, e
// toda remuneração lançada à mão nascia sem ela — R$ 19,9 mil em set/2026. Sem
// a pessoa o valor não é dividido pela ficha (Lucas 70/30, Marina 100%
// Administrativo) nem pelas horas que a pessoa anotou: desde 06/10/2026 ele
// fica PARADO, fora dos contratos, e aparece nos avisos.
//
// É o espelho de dre-engine/handlers/pessoaDoLancamento.js (o servidor decide;
// a tela mostra a mesma coisa ANTES de salvar). O teste roda as duas contra os
// mesmos casos — se uma mudar sozinha, ele cai.

export type Ficha = {
  id: string;
  nome_canonical: string;
  ativo?: boolean | null;
  aliases?: string[] | null;
};

/** Linhas da DRE que são pagamento a GENTE DA EQUIPE. Terceirizado e diarista
 *  ficam de fora de propósito: quase sempre é empresa ou pessoa sem ficha. */
export const LINHAS_REMUNERACAO_EQUIPE: readonly string[] = [
  'Remuneração de Sócios',
  'Remuneração de Autônomos',
  'Remuneração de Estagiários',
  'Remuneração CLT',
  'Pró-labore',
];

/** Valor do seletor para "Não é da equipe" — vai ao servidor como null. */
export const NINGUEM = '__ninguem__';

export const pedeAPessoa = (linha?: string | null): boolean =>
  LINHAS_REMUNERACAO_EQUIPE.includes(String(linha || ''));

/** Mesmo nome escrito de dois jeitos é o mesmo nome: sem acento, sem caixa, um
 *  espaço só. Não abrevia nem tira sobrenome. */
export function normalizarNome(s?: string | null): string {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

/** A ficha que o nome aponta — ou null quando não há, ou há mais de uma.
 *  Ficha ativa tem preferência; quem já saiu ainda é achado. */
export function fichaPeloNome(nome: string | null | undefined, fichas: Ficha[]): Ficha | null {
  const alvo = normalizarNome(nome);
  if (!alvo) return null;
  const casa = (f: Ficha) => [f.nome_canonical, ...(Array.isArray(f.aliases) ? f.aliases : [])]
    .some(n => normalizarNome(n) === alvo);
  const achadas = (fichas || []).filter(casa);
  const ativas = achadas.filter(f => !!f.ativo);
  if (ativas.length === 1) return ativas[0];
  if (ativas.length === 0 && achadas.length === 1) return achadas[0];
  return null;
}

/** A ficha que o nome do fornecedor sugere (o id, ou ''), só em linha de
 *  remuneração de gente da equipe — para quem ainda não escolheu. */
export function sugestaoDePessoa(linha: string | null | undefined, fornecedor: string | null | undefined, fichas: Ficha[]): string {
  if (!pedeAPessoa(linha)) return '';
  const f = fichaPeloNome(fornecedor, fichas);
  return f ? f.id : '';
}

/** Opções do seletor: quem está na equipe primeiro, depois quem saiu, e por
 *  último "Não é da equipe". */
export function opcoesDePessoa(fichas: Ficha[]): { value: string; label: string }[] {
  const ordem = (fichas || []).slice().sort((a, b) =>
    (a.ativo === b.ativo ? 0 : a.ativo ? -1 : 1) || a.nome_canonical.localeCompare(b.nome_canonical, 'pt-BR'));
  return [
    ...ordem.map(f => ({ value: f.id, label: f.ativo ? f.nome_canonical : `${f.nome_canonical} (saiu)` })),
    { value: NINGUEM, label: 'Não é da equipe (terceiro, sem ficha)' },
  ];
}

/**
 * O que mandar ao servidor em `profissional_id`.
 *   - escolha vazia → undefined (não fala do assunto: o servidor tenta o nome,
 *     só em linha de equipe)
 *   - NINGUEM       → null ("ninguém", e o servidor não adivinha por cima)
 *   - id            → o id
 */
export function pessoaParaEnviar(escolha?: string | null): string | null | undefined {
  if (!escolha) return undefined;
  if (escolha === NINGUEM) return null;
  return escolha;
}

/**
 * Na EDIÇÃO: o pedaço do salvar que fala da pessoa. Só fala quando o campo
 * difere do que está gravado — quem só mudou o vencimento não mexe na pessoa
 * (nem apaga a que existe, nem grava por cima). "Não é da equipe" vira null.
 */
export function campoDaPessoaNoSalvar(gravada: string | null | undefined, noCampo: string | null | undefined): { profissional_id?: string | null } {
  const antes = gravada || '';
  const agora = noCampo || '';
  if (antes === agora) return {};
  // sem pessoa gravada + "Não é da equipe" = continua sem pessoa: não há o que mandar
  if (!antes && agora === NINGUEM) return {};
  return { profissional_id: pessoaParaEnviar(agora) ?? null };
}

/** Pode limpar o campo no "x"? Em remuneração de equipe NÃO: um clique sem
 *  querer desligaria a pessoa (e a despesa sairia da divisão da ficha). Para
 *  dizer que não é ninguém existe a opção "Não é da equipe". */
export const podeLimparPessoa = (linha?: string | null): boolean => !pedeAPessoa(linha);

/**
 * Falta escolher? Só cobra quando a linha é de gente da equipe E a lista de
 * fichas carregou — se ela não carregou, a pessoa não tem como escolher, e
 * travar o lançamento de um salário por isso seria pior que deixar o servidor
 * tentar pelo nome.
 */
export function faltaEscolherPessoa(o: { linha?: string | null; escolha?: string | null; fichasCarregadas: boolean }): boolean {
  return pedeAPessoa(o.linha) && o.fichasCarregadas && !o.escolha;
}

/** O texto que explica o campo, conforme o estado. */
export function textoDaPessoa(o: { linha?: string | null; escolha?: string | null; sugerida: boolean }): string {
  if (o.escolha === NINGUEM) {
    return 'Fica sem pessoa: o valor fica parado, sem chegar a contrato nenhum, e aparece nos avisos. '
      + 'Se quem recebeu é um terceiro, a categoria certa costuma ser Honorários Terceirizados.';
  }
  // categoria que não é remuneração de equipe (a pessoa ficou de uma escolha anterior)
  if (o.escolha && !pedeAPessoa(o.linha)) return 'A pessoa fica registrada na despesa; só remuneração é dividida pelas horas dela (ou pela divisão da ficha).';
  if (o.escolha && o.sugerida) return 'Sugerida pelo nome do fornecedor: ao salvar, a despesa fica ligada a ela. Confira.';
  if (o.escolha) return 'É a pessoa que leva esta remuneração aos contratos: ela divide esta remuneração pelas horas que anotar no mês (ou pela divisão fixa da ficha).';
  if (pedeAPessoa(o.linha)) return 'Escolha quem recebeu. Sem a pessoa, este valor fica parado: não chega a contrato nenhum.';
  return '';
}
